import { useCallback, useEffect, useState } from 'react';
import { GridApi } from 'ag-grid-community';

import { pollSetupDeltasV2 } from 'modules/api/setup';
import { isCanceledError } from 'modules/api/request';
import { cancelFunctions } from 'utils/cancelFunctions';
import { SetupFrameInformation } from '../Setup.types';
import { applySetupDeltas, getSetupRows, hasPollingStateRows, SETUP_POLL_INTERVAL_MS } from './setupUtils';

interface UseSetupPollDeltasProps {
  isAssetTemplateV2Enabled: boolean | null;
  tableApiRef: React.MutableRefObject<GridApi | undefined>;
}

interface UseSetupPollDeltasResult {
  deltas: SetupFrameInformation[];
  startPolling: () => void;
}

export const useSetupPollDeltas = ({
  isAssetTemplateV2Enabled,
  tableApiRef,
}: UseSetupPollDeltasProps): UseSetupPollDeltasResult => {
  const [deltas, setDeltas] = useState<SetupFrameInformation[]>([]);

  const stopPolling = useCallback((): void => {
    cancelFunctions.SETUP_POLL_DELTAS_URL_V2?.();
  }, []);

  const startPolling = useCallback((): void => {
    if (!isAssetTemplateV2Enabled) {
      return;
    }

    stopPolling();

    const poll = pollSetupDeltasV2(cancelFunctions);

    poll((response: { data: { setupListResponse?: SetupFrameInformation[] } }) => {
      const gridApi = tableApiRef.current;

      if (!gridApi) {
        return false;
      }

      const { setupListResponse = [] } = response.data ?? {};

      if (setupListResponse.length) {
        setDeltas(setupListResponse);
        applySetupDeltas(gridApi, setupListResponse);
      }

      return hasPollingStateRows(getSetupRows(gridApi));
    }, SETUP_POLL_INTERVAL_MS).catch((error: unknown) => {
      if (isCanceledError(error)) return;

      setTimeout(startPolling, SETUP_POLL_INTERVAL_MS);
    });
  }, [isAssetTemplateV2Enabled, stopPolling, tableApiRef]);

  useEffect(() => {
    if (!isAssetTemplateV2Enabled) {
      stopPolling();
    }

    return stopPolling;
  }, [isAssetTemplateV2Enabled, stopPolling]);

  return {
    deltas,
    startPolling,
  };
};
